import { Popover } from 'antd';
import { memo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { StyledPopMarkdown } from '../atom';
import { useLanguage, useNotification } from 'src/service';
import { VersionLogButtonLabel } from './styled';
import { PUBLIC_PATH } from 'src/model';

const LogSentence = styled.li`
    text-align: center;
    .content {
        text-align: left;
    }
    a {
        display: inline-block;
    }
    img {
        display: block;
        max-width: 400px;
        max-height: 300px;
        margin: auto;
        box-shadow: var(--bs-1);
        margin-top: var(--spacing-xs);
    }
`;

type VersionLog = { version: string, logList: { content: string, image?: string }[] }[];
const VersionLogStore = (() => {
    let isReady = false;
    let versionLog: VersionLog = [];

    return {
        getLog: async () => {
            if (isReady) return versionLog;
            const response = await fetch(
                `${PUBLIC_PATH}/asset/log/version.json`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }
            );
            try {
                const responseLog = await response.json();
                versionLog = responseLog;
                isReady = true;

                return versionLog;
            } catch (e) {
                console.error(e);
            }
            return [];
        }
    };
})();
export const VersionInformation = () => {
    const language = useLanguage();
    const [log, setLog] = useState<VersionLog>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const log = await VersionLogStore.getLog();

            setLoading(false);
            setLog(log);
        })();
    }, []);

    if (log.length === 0 && !loading) return <div>{language['version-log.empty']}</div>;
    if (loading) return <div>{language['version-log.loading']}</div>;
    return <div>
        {log.map(({ logList, version }, index) => {
            return <div key={`${version}-${index}`} className="log-section">
                <b>{version}</b><br />
                <ul>
                    {/* Using index as key here is safe as the component is stateless */}
                    {logList.map(({ content, image }, index) => {
                        return <LogSentence key={index}>
                            <div className="content">
                                {content}
                            </div>
                            {image && <a target="_blank" rel="noreferrer" href={image} title={language['version-log.open-image.tooltip']}>
                                <img src={image} alt={`version-${version}-illust`} />
                            </a>}
                        </LogSentence>;
                    })}
                </ul>
            </div>;
        })}
    </div>;
};

const isSemverHigher = (a: string, b: string): boolean => {
    const [majorA, minorA, patchA] = String(a).split('.').map(Number);
    const [majorB, minorB, patchB] = String(b).split('.').map(Number);

    if (majorA !== majorB) {
        return majorA > majorB;
    }

    if (minorA !== minorB) {
        return minorA > minorB;
    }

    return patchA > patchB;
};

const StyledVersionLog = styled(StyledPopMarkdown)`
    overflow: auto;
    width: 550px;
    max-height: 300px;
    width: 550px;
    .log-section + .log-section {
        margin-top: var(--spacing);
    }
`;
export const VersionLogButton = memo(() => {
    const [
        version,
        setMemoizedVersion,
    ] = useNotification('versionReminder');
    const [animating, setAnimating] = useState(() => {
        const currentSemver = import.meta.env.APP_VERSION ?? '0.0.0';
        if (version && isSemverHigher(currentSemver, version)) {
            return true;
        }
        return false;
    });

    useEffect(() => {
        if (import.meta.env.APP_VERSION) setMemoizedVersion(import.meta.env.APP_VERSION);

        setTimeout(() => {
            setAnimating(false);
        }, 8000);
    }, [setMemoizedVersion]);

    return <Popover
        placement="bottom"
        content={<StyledVersionLog>
            <VersionInformation />
        </StyledVersionLog>}
    >
        <VersionLogButtonLabel $animating={animating} className="app-log" onMouseOver={() => setAnimating(false)}>
            v{import.meta.env.APP_VERSION ?? 'unknown'}
        </VersionLogButtonLabel>
    </Popover>;
});
